import NewFromController from "csi/action/NewFromController";
import html from "csi/html";
import { FormStore, NewFormDialog } from "csi/middleware/form/index";
import reflection from "csi/reflection";
import { shouldUseReactForm } from "csi/service/viewService";
import { routerStore } from "csi/state/rootStore";
import { cnl, isMapValueType } from "csi/typings";
import { PARENT_PATH } from "csi/utility/pathUtility";
import xml from "csi/xml";
import declare from "dojo/_base/declare";
import { getFormStateByNodeType, IGetForm } from "src/c8-platform/service/formService";
import { ActionView } from "./ActionView";
import FormData from "./FormData";

const CopyController = declare(NewFromController, {

	bInsertAfter: true,

	// String: relation from source node's owner (__Parent__) to source node
	idOwnerRelation: null,

	// String: source node's owner (__Parent__) URL
	urlOwnerParent: null,

	// Boolean: true if copying a group
	_bGroup: false,

	constructor: function (/*Object*/params) {
		// summary: get the node we acted upon - MoveFromController resets it to null
		const view = this.widget;
		let refNodeId;

		if (this.refNode) {
			this.m_urlNode = view.getRefNodeUrl(this.refNode);
			refNodeId = view.getRefNodeId(this.refNode);
		} else {
			this.m_urlNode = html.getElementContext(this.m_element);
		}

		// handle copying of groups
		const qr = refNodeId && view.getQueryResult && view.getQueryResult(refNodeId);
		if (qr && qr.isGroup()) {
			this._bGroup = true;
			this.m_urlNode = qr.groupValue;
		}

		const node = this.getNode();
		this.m_aSources = [node];
		this._sourceSelectedLength = 1;
		this.urlOwnerParent = node.__Parent__;

		// by default use separate request to query for copied node and any dependencies
		this.m_action.setReturnParameters();
	},

	/**
	 * @override
	 * Specify path to get to parent Collection
	 */
	onDependencyPaths(paths) {
		paths.push(PARENT_PATH);
	},

	/**
	 * @override
	 * call mixin before continuing
	 */
	preProcess() {
		const node = this.getNode();
		this.useReactForm = shouldUseReactForm(node.$Type);
		if (this.useReactForm) {
			this.preProcessReactForm();
			return;
		}
		this.inherited(arguments);
	},

	/** preProcess for the new React form */
	preProcessReactForm() {
		this.suspendProcess();
		const { idOwnerRelation, m_idAttrParent, m_urlParent, urlOwnerParent } = this;
		const node = this.getNode();

		const parent = this.m_cache.get(urlOwnerParent);
		const relation = idOwnerRelation ? reflection.getRelation(parent, idOwnerRelation) : reflection.getRelationBetweenNodes(node, parent, true);
		const parentAttributeId = relation?.attributeId;

		const form: IGetForm = {
			nodeType: node.$Type,
			parentAttributeId,
			parentUrl: urlOwnerParent,
			sourceNodeUrl: node.$URL,
		};

		if (m_urlParent !== urlOwnerParent) {
			form.contextAttributeId = m_idAttrParent;
			form.contextUrl = m_urlParent;
		}

		const formInfo = getFormStateByNodeType(form);

		const processFormCallback = (formNodeCNL: cnl, bNavigate: boolean, bRepeat: boolean): void => {
			this.m_urlNode = formNodeCNL;
			this.m_value = [formNodeCNL];
			this.resumeProcess();
			this.postProcess(true);
		};

		const processCancelFormCallback = (): void => {
			this.repeatReactForm = false;
			this.onCancelReactDialog();
		};

		formInfo.then((formInfo) => {
			const cache = this.getCache();
			const store = new FormStore(
				cache,
				formInfo,
				processFormCallback,
				processCancelFormCallback,
				false,
				false,
			);

			this.useReactDialog(routerStore.openDialog(NewFormDialog, { store, title: this.getTitle() }));
		});
	},

	postProcessSuccess() {
		// summary: handle copying of groups
		if (this._bGroup) {
			return this.widget.refreshData();
		}
		return this.inherited(arguments);
	},

	/** Not needed for the new React Form */
	addProxyActions(/*View*/defView, /*Actions*/defActions) {
		// summary: if creating new node, then add Save & Copy action as well
		if (this._canRepeat()) {
			defActions.addAction(this.getProxyAction("Save_Copy", true, { m_bRepeat: true }));
		}

		defActions.addAction(this.getProxyAction("Save", true, { m_bSubmit: true }));
		this.inherited(arguments);
	},

	/** Not needed for the new React Form */
	getActionView(/*ViewRef?*/vr) {
		// summary: use source node for form view
		const av = this.inherited<ActionView>(arguments);
		if (av) {
			av.m_node = this.m_aSources[0];
		}

		return av;
	},

	/** Not needed for the new React Form */
	getFormData() {
		// summary: indicate that form is creating new node
		const fd = this.inherited<FormData>(arguments);

		// reset node since creating new node from source
		fd.m_node = null;

		// trick csi.form.Widget.getFormNode into using actual parent node rather than table root node (SupplierRequest)
		if (this.idOwnerRelation && xml.isValidURL(this.urlOwnerParent)) {
			fd.m_parentNode = this.m_cache.getNode(this.urlOwnerParent);
		}

		return fd;
	},

	_canRename(/*Node*/source) {
		// summary: check force rename property
		if (this.m_action.forceRename == 0) {
			return false;
		}

		return this.m_action.forceRename == 1 || this.inherited(arguments);
	},

	_canRepeat() {
		// summary: allow Save & Copy if no relation (global nodes) or relation is container; also disallow if copying a group -
		// the table does not support incrementally adding groups, so view must be refreshed for each one added which would mean
		// the refNode is no longer valid (and it may be hard to find again since it uses a client URL which is not consistent)
		return !this.bPreventRepeat && !this._bGroup && !this.widget.defView.idContentView && (!this.relation || xml.attributeTypeIsContainer(this.relation.getType()));
	},

	/**
	 * Return refNode which new node(s) will be inserted before
	 * @return {DOMNode}
	 */
	_getRefNodeNext() {
		return this.widget.getRefNodeNext(this.refNode);
	},

	/**
	 * Return refNode which new node(s) will be inserted after
	 * @return {DOMNode}
	 */
	_getRefNodePrev() {
		return this.refNode;
	},

	/**
	 * @override
	 * Specify form info
	 */
	_onCreatePopupParams(viewParams) {
		const { idOwnerRelation, m_idAttrParent, m_urlParent, urlOwnerParent } = this;
		const node = this.getNode();
		const parent = this.m_cache.get(urlOwnerParent);
		const relation = idOwnerRelation ? reflection.getRelation(parent, idOwnerRelation) : reflection.getRelationBetweenNodes(node, parent, true);
		const parentAttributeId = relation?.attributeId;

		// if copying a node that will be stored in a map (probably very rare), then make sure to attach to the parent node with some key;
		// for aggregate store report operations, it does not have to be the real key - just some dummy string to make the operation valid
		const parentAttributeKey = isMapValueType(relation?.valueType) ? "__Copy__" : undefined;

		const formInfo: any = viewParams.formInfo = {
			// nodeUrl: <eventually specify new url here rather than create in form, but beware of Save & Copy>
			nodeType: node.$Type,
			parentAttributeId,
			parentAttributeKey,
			parentUrl: urlOwnerParent,
			sourceNodeUrl: node.$URL,
		};

		if (m_urlParent !== urlOwnerParent) {
			formInfo.contextAttributeId = m_idAttrParent;
			formInfo.contextUrl = m_urlParent;
		}

		this.inherited(arguments);
	},

	_toWriterParent() {
		// summary: if source node is owned by node other than one in current context (m_urlParent), then also add copied node to real parent
		this.inherited(arguments);

		var owner = this.idOwnerRelation ? this.m_cache.getNode(this.urlOwnerParent) : null;
		if (owner) {
			var relation = reflection.getRelation(owner, this.idOwnerRelation);
			if (relation) {
				this.m_writer.changeParent(this.urlOwnerParent, this.idOwnerRelation, relation.getType(), this.m_value, true);
			}
		}
	},
});

export = CopyController;
